// S37 生態系治理(/ecosystems)— Ecosystem governance(IA specs/01 §2.4;admin#6)。
// ①審核佇列:創建申請(org 第 2+ 個 custom ecosystem 的 pending_review 核准/駁回,
// reason+case 必填、二段確認、審核歷史)+ 加入申請監督(跨 org join,admin 唯讀監督;
// 核准權在 owner org portal)②生態系 inventory(kind/status/owner org 篩選、owner org
// 巢狀分組、lineage 下鑽 + 直接成員清單 + effective 遞移展開)③平台治理 suspend
// (kinio.ai=platform 不提供破壞性操作)。
//
// admin#6 三個 read model 缺口已補齊(restful /{id}/members、/{id}/effective、
// ecosystem-requests?type=join):成員清單=直接成員 org + 巢狀子生態系;effective 展開=
// 重用 enforcement resolver 的 BFS(via-chain + depth + truncated,active-only、深度上限 3);
// join 監督=admin 唯讀看跨 org join 申請(核准/駁回動作仍屬 owner org)。皆唯讀、不繞授權。

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { ApiError } from '../../lib/api';
import { ErrorState } from '../../components/StateBlock';
import { ConfirmDialog } from '../_shared/ConfirmDialog';
import { formatTimestampUs } from '../../lib/format';
import { roleLabel } from '../users/userModel';
import '../_shared/shared.css';
import './ecosystem.css';
import type { Ecosystem, EcosystemEffective, EcosystemMembersView, EcosystemRequest } from './ecosystemApi';
import {
  approveEcosystemRequest,
  getEcosystemEffective,
  getEcosystemMembers,
  listEcosystemRequests,
  listEcosystems,
  rejectEcosystemRequest,
  suspendEcosystem,
} from './ecosystemApi';
import {
  KIND_OPTIONS,
  REQUEST_STATUS_OPTIONS,
  REQUEST_TYPE_OPTIONS,
  STATUS_OPTIONS,
  canEcosystemWrite,
  canSuspend,
  ecoKindLabel,
  ecoKindTone,
  ecoStatusLabel,
  ecoStatusTone,
  findCreateRequest,
  formatViaChain,
  groupByOwnerOrg,
  isPendingRequest,
  isPlatformEcosystem,
  memberStatusLabel,
  memberStatusTone,
  requestStatusLabel,
  requestStatusTone,
  requestTypeLabel,
  summarizeInventory,
} from './ecosystemModel';

function errorText(e: unknown): string {
  if (e instanceof ApiError) return `${e.message}${e.requestId ? `（request_id: ${e.requestId}）` : ''}`;
  return e instanceof Error ? e.message : '操作失敗';
}

type Tab = 'queue' | 'inventory';
type DecisionKind = 'approve' | 'reject';

export default function EcosystemsPage() {
  const { role } = useAuth();
  const writable = canEcosystemWrite(role);
  const [tab, setTab] = useState<Tab>('queue');

  // ---- 審核佇列 ----
  const [reqType, setReqType] = useState<'create' | 'join'>('create');
  const [reqStatus, setReqStatus] = useState('pending');
  const [requests, setRequests] = useState<EcosystemRequest[]>([]);
  const [reqLoading, setReqLoading] = useState(false);
  const [reqErr, setReqErr] = useState<unknown>(null);
  const [selectedReqId, setSelectedReqId] = useState('');
  const [decideReason, setDecideReason] = useState('');
  const [decideCase, setDecideCase] = useState('');
  const [confirmDecision, setConfirmDecision] = useState<DecisionKind | null>(null);

  // ---- inventory ----
  const [invKind, setInvKind] = useState('');
  const [invStatus, setInvStatus] = useState('');
  const [invOwnerOrg, setInvOwnerOrg] = useState('');
  const [ecosystems, setEcosystems] = useState<Ecosystem[]>([]);
  const [invLoaded, setInvLoaded] = useState(false);
  const [invLoading, setInvLoading] = useState(false);
  const [invErr, setInvErr] = useState<unknown>(null);
  const [grouped, setGrouped] = useState(false);
  const [selectedEcoId, setSelectedEcoId] = useState('');
  const [lineageRequests, setLineageRequests] = useState<EcosystemRequest[]>([]);
  const [suspendReason, setSuspendReason] = useState('');
  const [suspendCase, setSuspendCase] = useState('');
  const [confirmSuspend, setConfirmSuspend] = useState(false);

  // ---- admin#6 read model:選取生態系的直接成員 + effective 遞移展開 ----
  const [memberView, setMemberView] = useState<EcosystemMembersView | null>(null);
  const [memberErr, setMemberErr] = useState<unknown>(null);
  const [memberLoading, setMemberLoading] = useState(false);
  const [effective, setEffective] = useState<EcosystemEffective | null>(null);
  const [effErr, setEffErr] = useState<unknown>(null);
  const [effLoading, setEffLoading] = useState(false);
  const [showEffective, setShowEffective] = useState(false);

  // ---- 共用 ----
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [actionErr, setActionErr] = useState('');

  const loadRequests = useCallback(async (status: string, type: 'create' | 'join') => {
    setReqLoading(true);
    setReqErr(null);
    setNotice('');
    setActionErr('');
    try {
      const rows = await listEcosystemRequests(status, type);
      setRequests(rows);
      setSelectedReqId('');
    } catch (e) {
      setReqErr(e);
      setRequests([]);
    } finally {
      setReqLoading(false);
    }
  }, []);

  const loadInventory = useCallback(async () => {
    setInvLoading(true);
    setInvErr(null);
    setNotice('');
    setActionErr('');
    try {
      const rows = await listEcosystems({ kind: invKind, status: invStatus, owner_org: invOwnerOrg });
      setEcosystems(rows);
      setInvLoaded(true);
      setSelectedEcoId('');
      // lineage 交叉比對用的全量創建紀錄(best-effort;失敗不阻擋 inventory)
      try {
        setLineageRequests(await listEcosystemRequests('all'));
      } catch {
        setLineageRequests([]);
      }
    } catch (e) {
      setInvErr(e);
      setEcosystems([]);
      setInvLoaded(true);
    } finally {
      setInvLoading(false);
    }
  }, [invKind, invStatus, invOwnerOrg]);

  useEffect(() => {
    void loadRequests('pending', 'create');
  }, [loadRequests]);

  // 首次切到 inventory tab 時載入
  useEffect(() => {
    if (tab === 'inventory' && !invLoaded && !invLoading) void loadInventory();
  }, [tab, invLoaded, invLoading, loadInventory]);

  const loadMembers = useCallback(async (ecosystemId: string) => {
    setMemberLoading(true);
    setMemberErr(null);
    setMemberView(null);
    try {
      setMemberView(await getEcosystemMembers(ecosystemId));
    } catch (e) {
      setMemberErr(e);
    } finally {
      setMemberLoading(false);
    }
  }, []);

  // 選取生態系 → 載入直接成員(admin#6);切換選取時重置 effective 展開。
  useEffect(() => {
    setEffective(null);
    setEffErr(null);
    setShowEffective(false);
    if (!selectedEcoId) {
      setMemberView(null);
      setMemberErr(null);
      return;
    }
    void loadMembers(selectedEcoId);
  }, [selectedEcoId, loadMembers]);

  const loadEffective = useCallback(async (ecosystemId: string) => {
    setEffLoading(true);
    setEffErr(null);
    setShowEffective(true);
    try {
      setEffective(await getEcosystemEffective(ecosystemId));
    } catch (e) {
      setEffErr(e);
      setEffective(null);
    } finally {
      setEffLoading(false);
    }
  }, []);

  const selectedReq = useMemo(() => requests.find((r) => r.request_id === selectedReqId) ?? null, [requests, selectedReqId]);
  const selectedEco = useMemo(() => ecosystems.find((e) => e.ecosystem_id === selectedEcoId) ?? null, [ecosystems, selectedEcoId]);
  const selectedEcoLineage = useMemo(
    () => (selectedEco ? findCreateRequest(lineageRequests, selectedEco.ecosystem_id) : undefined),
    [selectedEco, lineageRequests]
  );

  const reqPending = requests.filter((r) => r.status === 'pending').length;
  const reqApproved = requests.filter((r) => r.status === 'approved').length;
  const reqRejected = requests.filter((r) => r.status === 'rejected').length;
  const invSummary = useMemo(() => summarizeInventory(ecosystems), [ecosystems]);
  const ownerGroups = useMemo(() => groupByOwnerOrg(ecosystems), [ecosystems]);

  const decideReady = Boolean(writable && selectedReq && isPendingRequest(selectedReq) && decideReason.trim() && decideCase.trim());
  const suspendReady = Boolean(writable && selectedEco && canSuspend(selectedEco) && suspendReason.trim() && suspendCase.trim());

  async function submitDecision() {
    if (!selectedReq || !confirmDecision) return;
    setBusy(true);
    try {
      const audit = { reason: decideReason.trim(), case_id: decideCase.trim() };
      const next = confirmDecision === 'approve'
        ? await approveEcosystemRequest(selectedReq.request_id, audit)
        : await rejectEcosystemRequest(selectedReq.request_id, audit);
      setRequests((rows) => rows.map((r) => (r.request_id === next.request_id ? next : r)));
      setConfirmDecision(null);
      setActionErr('');
      setNotice(
        `已${confirmDecision === 'approve' ? '核准' : '駁回'}創建申請 ${next.request_id}` +
          `${next.ecosystem_id ? `(生態系 ${next.ecosystem_id}${confirmDecision === 'approve' ? ' → 生效中' : ' → 已駁回'})` : ''}`
      );
      setDecideReason('');
      setDecideCase('');
      // 若目前僅看 pending,決議後該筆會移出清單 → 重載保持一致
      if (reqStatus === 'pending') void loadRequests('pending', reqType);
    } catch (e) {
      setActionErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function submitSuspend() {
    if (!selectedEco) return;
    setBusy(true);
    try {
      const next = await suspendEcosystem(selectedEco.ecosystem_id, { reason: suspendReason.trim(), case_id: suspendCase.trim() });
      setEcosystems((rows) => rows.map((e) => (e.ecosystem_id === next.ecosystem_id ? next : e)));
      setConfirmSuspend(false);
      setActionErr('');
      setNotice(`已停權生態系 ${next.ecosystem_id}(${ecoStatusLabel(next.status)})`);
      setSuspendReason('');
      setSuspendCase('');
    } catch (e) {
      setActionErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="eco-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">生態系治理</h1>
          <p className="page-subtitle">
            org 互通邊界(ecosystem)的平台治理:創建審核佇列、清冊瀏覽、平台級停權。核准權在此、join 核准權在 owner org。
          </p>
        </div>
        <div className="adm-actionbar-btns">
          <span className={`pill ${writable ? 'pill-good' : 'pill-neutral'}`}>{role ? roleLabel(role) : '其他角色'}</span>
        </div>
      </div>

      {notice && (
        <p className="adm-action-result is-ok" role="status">
          {notice}
        </p>
      )}
      {actionErr && !confirmDecision && !confirmSuspend && (
        <p className="adm-action-result is-err" role="alert">
          {actionErr}
        </p>
      )}

      <div className="adm-tabs" role="tablist" aria-label="生態系治理分頁">
        <button role="tab" aria-selected={tab === 'queue'} className={`adm-tab${tab === 'queue' ? ' is-active' : ''}`} onClick={() => setTab('queue')}>
          創建審核佇列
        </button>
        <button role="tab" aria-selected={tab === 'inventory'} className={`adm-tab${tab === 'inventory' ? ' is-active' : ''}`} onClick={() => setTab('inventory')}>
          生態系清冊
        </button>
      </div>

      {tab === 'queue' ? (
        <>
          <div className="kpi-strip" aria-label="審核統計">
            <div className="kpi-card kpi-card--hero">
              <span className="kpi-label">待審核</span>
              <span className="kpi-value">{reqPending}</span>
              <span className="kpi-foot">{reqType === 'create' ? 'pending(第 2+ 個 custom 生態系)' : 'pending(跨 org join,owner org 待核)'}</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-label">已核准</span>
              <span className="kpi-value">{reqApproved}</span>
              <span className="kpi-foot">approved</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-label">已駁回</span>
              <span className="kpi-value">{reqRejected}</span>
              <span className="kpi-foot">rejected</span>
            </div>
            <div className="kpi-card kpi-card--secondary">
              <span className="kpi-label">目前清單</span>
              <span className="kpi-value">{requests.length}</span>
              <span className="kpi-foot">{REQUEST_STATUS_OPTIONS.find((o) => o.value === reqStatus)?.label}</span>
            </div>
          </div>

          <div className="eco-split">
            <section className="card">
              <div className="section-head">
                <div>
                  <h2 className="section-title">{reqType === 'create' ? '創建審核佇列' : '加入申請監督'}</h2>
                  <span className="section-meta">
                    {reqType === 'create'
                      ? 'org 申請的第 2+ 個(custom)ecosystem;第 1 個 org_default 免審直接生效。'
                      : '跨 org 的生態系 join 申請;核准/駁回權在被加入方 owner org(portal),admin 此處僅監督 + 稽核。'}
                  </span>
                </div>
                {reqLoading && <span className="pill pill-neutral">查詢中</span>}
              </div>

              <div className="adm-tabs adm-tabs--sub" role="tablist" aria-label="申請類型">
                {REQUEST_TYPE_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    role="tab"
                    aria-selected={reqType === o.value}
                    className={`adm-tab${reqType === o.value ? ' is-active' : ''}`}
                    onClick={() => {
                      if (reqType === o.value) return;
                      const next = o.value as 'create' | 'join';
                      setReqType(next);
                      setSelectedReqId('');
                      setActionErr('');
                      void loadRequests(reqStatus, next);
                    }}
                  >
                    {o.label}
                  </button>
                ))}
              </div>

              <div className="eco-filter-row">
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">狀態</span>
                  <select
                    value={reqStatus}
                    onChange={(e) => {
                      setReqStatus(e.target.value);
                      void loadRequests(e.target.value, reqType);
                    }}
                  >
                    {REQUEST_STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                <button className="btn-ghost btn-sm" disabled={reqLoading} onClick={() => void loadRequests(reqStatus, reqType)}>
                  重新整理
                </button>
              </div>

              {reqErr ? (
                <ErrorState error={reqErr} onRetry={() => void loadRequests(reqStatus, reqType)} />
              ) : requests.length > 0 ? (
                <div className="table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>申請</th>
                        <th>申請組織</th>
                        <th>{reqType === 'create' ? '生態系' : '加入關係'}</th>
                        <th>理由</th>
                        <th>狀態</th>
                        <th>{reqType === 'create' ? '審核' : 'owner org / 決議'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requests.map((req) => (
                        <tr
                          key={req.request_id}
                          className={`clickable ${selectedReqId === req.request_id ? 'is-selected' : ''}`}
                          onClick={() => {
                            setSelectedReqId(req.request_id === selectedReqId ? '' : req.request_id);
                            setActionErr('');
                            setDecideReason('');
                            setDecideCase('');
                          }}
                        >
                          <td>
                            <div className="adm-mono" title={req.request_id}>
                              {req.request_id.slice(0, 16)}…
                            </div>
                            <div className="text-tertiary">
                              {requestTypeLabel(req.type)}・{formatTimestampUs(req.created_at, { second: undefined })}
                            </div>
                          </td>
                          <td className="adm-mono">{req.applicant_org_id}</td>
                          {req.type === 'join' ? (
                            <td className="adm-mono">
                              {req.applicant_ecosystem_id || '—'} → {req.target_ecosystem_id || '—'}
                            </td>
                          ) : (
                            <td className="adm-mono">{req.ecosystem_id || '—'}</td>
                          )}
                          <td>{req.reason || '—'}</td>
                          <td>
                            <span className={`pill ${requestStatusTone(req.status)}`}>{requestStatusLabel(req.status)}</span>
                          </td>
                          {req.type === 'join' ? (
                            <td>
                              <div className="adm-mono">{req.target_owner_org_id || '—'}</div>
                              <div className="text-tertiary">{req.decided_by ? `決議:${req.decided_by}` : '待 owner 核准'}</div>
                            </td>
                          ) : (
                            <td>
                              <div>{req.decided_by || '—'}</div>
                              <div className="text-tertiary">{req.case_id || ''}</div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state">
                  <div className="empty-state-icon">✅</div>
                  <div className="empty-state-title">
                    目前沒有{REQUEST_STATUS_OPTIONS.find((o) => o.value === reqStatus)?.label}的{reqType === 'create' ? '創建' : '加入'}申請
                  </div>
                  <div className="empty-state-detail">
                    {reqType === 'create'
                      ? 'org 申請第 2+ 個 custom 生態系時,會進入此佇列等待平台審核。'
                      : 'org 申請旗下生態系加入其他生態系時,會在此供 admin 監督;核准/駁回由被加入方 owner org 執行。'}
                  </div>
                </div>
              )}
            </section>

            <aside className="eco-side">
              <section className="card card--primary">
                <div className="section-head">
                  <h2 className="section-title">{selectedReq ? (reqType === 'create' ? '審核決議' : '加入申請詳情') : '選取一筆申請'}</h2>
                </div>
                {!selectedReq ? (
                  <p className="eco-hint text-tertiary">
                    {reqType === 'create'
                      ? '在左側點選一筆創建申請以檢視詳情或進行核准/駁回。'
                      : '在左側點選一筆加入申請以檢視詳情;此類申請由 owner org 核准,admin 僅監督。'}
                  </p>
                ) : (
                  <div className="eco-form">
                    <div className="eco-kv-grid">
                      <div className="eco-kv">
                        <span className="eco-kv-label">申請 ID</span>
                        <span className="eco-kv-value adm-mono">{selectedReq.request_id}</span>
                      </div>
                      <div className="eco-kv">
                        <span className="eco-kv-label">申請組織</span>
                        <span className="eco-kv-value adm-mono">{selectedReq.applicant_org_id}</span>
                      </div>
                      {selectedReq.type === 'join' ? (
                        <>
                          <div className="eco-kv">
                            <span className="eco-kv-label">申請方生態系</span>
                            <span className="eco-kv-value adm-mono">{selectedReq.applicant_ecosystem_id || '—'}</span>
                          </div>
                          <div className="eco-kv">
                            <span className="eco-kv-label">加入目標生態系</span>
                            <span className="eco-kv-value adm-mono">{selectedReq.target_ecosystem_id || '—'}</span>
                          </div>
                          <div className="eco-kv">
                            <span className="eco-kv-label">核准方 owner org</span>
                            <span className="eco-kv-value adm-mono">{selectedReq.target_owner_org_id || '—'}</span>
                          </div>
                        </>
                      ) : (
                        <div className="eco-kv">
                          <span className="eco-kv-label">目標生態系</span>
                          <span className="eco-kv-value adm-mono">{selectedReq.ecosystem_id || '—'}</span>
                        </div>
                      )}
                      <div className="eco-kv">
                        <span className="eco-kv-label">申請時間</span>
                        <span className="eco-kv-value">{formatTimestampUs(selectedReq.created_at)}</span>
                      </div>
                      <div className="eco-kv">
                        <span className="eco-kv-label">申請理由</span>
                        <span className="eco-kv-value">{selectedReq.reason || '—'}</span>
                      </div>
                      <div className="eco-kv">
                        <span className="eco-kv-label">建立者</span>
                        <span className="eco-kv-value adm-mono">{selectedReq.created_by || '—'}</span>
                      </div>
                    </div>

                    {selectedReq.type === 'join' ? (
                      <>
                        {isPendingRequest(selectedReq) ? (
                          <p className="eco-hint text-tertiary">
                            此為跨 org 加入申請,核准/駁回權在被加入方 owner org(<span className="adm-mono">{selectedReq.target_owner_org_id || '—'}</span>)於
                            portal dev face 執行(D-EC5/D-EC6)。admin 此處僅監督 + 稽核,不代為核准。
                          </p>
                        ) : (
                          <div className="eco-kv-grid">
                            <div className="eco-kv">
                              <span className="eco-kv-label">決議</span>
                              <span className="eco-kv-value">
                                <span className={`pill ${requestStatusTone(selectedReq.status)}`}>{requestStatusLabel(selectedReq.status)}</span>
                              </span>
                            </div>
                            <div className="eco-kv">
                              <span className="eco-kv-label">決議人(owner org)</span>
                              <span className="eco-kv-value adm-mono">{selectedReq.decided_by || '—'}</span>
                            </div>
                            <div className="eco-kv">
                              <span className="eco-kv-label">決議時間</span>
                              <span className="eco-kv-value">{formatTimestampUs(selectedReq.decided_at)}</span>
                            </div>
                            <div className="eco-kv">
                              <span className="eco-kv-label">決議理由</span>
                              <span className="eco-kv-value">{selectedReq.decision_reason || '—'}</span>
                            </div>
                          </div>
                        )}
                      </>
                    ) : isPendingRequest(selectedReq) ? (
                      writable ? (
                        <>
                          <label className="adm-field">
                            <span className="adm-field-label text-secondary">處置理由(必填)</span>
                            <input value={decideReason} onChange={(e) => setDecideReason(e.target.value)} disabled={busy} placeholder="核准/駁回的原因" />
                          </label>
                          <label className="adm-field">
                            <span className="adm-field-label text-secondary">案件編號(必填)</span>
                            <input value={decideCase} onChange={(e) => setDecideCase(e.target.value)} disabled={busy} placeholder="CASE-…" />
                          </label>
                          <div className="adm-modal-actions" style={{ justifyContent: 'flex-start' }}>
                            <button
                              className="btn-primary"
                              disabled={busy || !decideReady}
                              onClick={() => {
                                setActionErr('');
                                setConfirmDecision('approve');
                              }}
                            >
                              核准
                            </button>
                            <button
                              className="btn-danger"
                              disabled={busy || !decideReady}
                              onClick={() => {
                                setActionErr('');
                                setConfirmDecision('reject');
                              }}
                            >
                              駁回
                            </button>
                          </div>
                          <span className="eco-hint text-tertiary">
                            核准=生態系 pending_review → 生效中;駁回=生態系 → 已駁回。兩者皆二段確認、reason+case 全鏈稽核
                            (admin.ecosystem.create_approved / _rejected)。ecosystem 審核不設 dual-control(申請 org 與審核者天然分離)。
                          </span>
                        </>
                      ) : (
                        <p className="eco-hint text-tertiary">此申請待審核;核准/駁回由 admin / platform-owner 執行(目前角色唯讀)。</p>
                      )
                    ) : (
                      <div className="eco-kv-grid">
                        <div className="eco-kv">
                          <span className="eco-kv-label">決議</span>
                          <span className="eco-kv-value">
                            <span className={`pill ${requestStatusTone(selectedReq.status)}`}>{requestStatusLabel(selectedReq.status)}</span>
                          </span>
                        </div>
                        <div className="eco-kv">
                          <span className="eco-kv-label">審核人</span>
                          <span className="eco-kv-value adm-mono">{selectedReq.decided_by || '—'}</span>
                        </div>
                        <div className="eco-kv">
                          <span className="eco-kv-label">審核時間</span>
                          <span className="eco-kv-value">{formatTimestampUs(selectedReq.decided_at)}</span>
                        </div>
                        <div className="eco-kv">
                          <span className="eco-kv-label">案件編號</span>
                          <span className="eco-kv-value adm-mono">{selectedReq.case_id || '—'}</span>
                        </div>
                        <div className="eco-kv">
                          <span className="eco-kv-label">決議理由</span>
                          <span className="eco-kv-value">{selectedReq.decision_reason || '—'}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </section>
            </aside>
          </div>
        </>
      ) : (
        <>
          <div className="kpi-strip" aria-label="生態系統計">
            <div className="kpi-card kpi-card--hero">
              <span className="kpi-label">生態系總數</span>
              <span className="kpi-value">{invLoaded ? invSummary.total : '—'}</span>
              <span className="kpi-foot">目前篩選結果</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-label">生效中</span>
              <span className="kpi-value">{invLoaded ? invSummary.active : '—'}</span>
              <span className="kpi-foot">active</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-label">待審核</span>
              <span className="kpi-value">{invLoaded ? invSummary.pendingReview : '—'}</span>
              <span className="kpi-foot">pending_review</span>
            </div>
            <div className="kpi-card kpi-card--secondary">
              <span className="kpi-label">已停權</span>
              <span className="kpi-value">{invLoaded ? invSummary.suspended : '—'}</span>
              <span className="kpi-foot">suspended</span>
            </div>
          </div>

          <p className="eco-gap-note">
            選取一個生態系即可下鑽:owner org 巢狀分組、生態系來源鏈、<strong>直接成員清單</strong>(成員 org + 巢狀子生態系)、
            以及 <strong>有效範圍展開</strong>(哪條鏈、在第幾層帶進哪個 org;重用 enforcement resolver 的 BFS,active-only、深度上限 3)。
            跨 org 的 join 申請監督在「審核佇列 → 加入申請」分頁。⚠️ effective 只計 active 生態系(suspended/pending 展開為空);
            join 的核准/駁回權仍在被加入方 owner org(portal),admin 的治理槓桿 = suspend。
          </p>

          <div className="eco-split">
            <section className="card">
              <div className="section-head">
                <div>
                  <h2 className="section-title">生態系清冊</h2>
                  <span className="section-meta">全平台 ecosystem(kinio.ai 平台生態系恆置頂)。</span>
                </div>
                {invLoading && <span className="pill pill-neutral">查詢中</span>}
              </div>

              <div className="eco-filter-row">
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">類型</span>
                  <select value={invKind} onChange={(e) => setInvKind(e.target.value)}>
                    {KIND_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">狀態</span>
                  <select value={invStatus} onChange={(e) => setInvStatus(e.target.value)}>
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">擁有組織</span>
                  <input
                    value={invOwnerOrg}
                    onChange={(e) => setInvOwnerOrg(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void loadInventory();
                    }}
                    placeholder="org-…"
                  />
                </label>
                <button className="btn-primary btn-sm" disabled={invLoading} onClick={() => void loadInventory()}>
                  套用篩選
                </button>
                <button
                  className={`btn-ghost btn-sm${grouped ? ' is-active' : ''}`}
                  onClick={() => setGrouped((g) => !g)}
                  aria-pressed={grouped}
                  title="依 owner org 巢狀分組檢視"
                >
                  {grouped ? '平面清單' : 'owner org 分組'}
                </button>
              </div>

              {invErr ? (
                <ErrorState error={invErr} onRetry={() => void loadInventory()} />
              ) : ecosystems.length > 0 ? (
                grouped ? (
                  <div className="eco-form">
                    {ownerGroups.map((group) => (
                      <div className="eco-org-group" key={group.ownerOrgId}>
                        <div className="eco-org-group-head">
                          <strong className="adm-mono">{group.ownerOrgId}</strong>
                          <span className="text-tertiary">{group.ecosystems.length} 個生態系</span>
                        </div>
                        <div className="table-wrap">
                          <table className="data-table">
                            <tbody>
                              {group.ecosystems.map((eco) => (
                                <tr
                                  key={eco.ecosystem_id}
                                  className={`clickable ${selectedEcoId === eco.ecosystem_id ? 'is-selected' : ''}`}
                                  onClick={() => {
                                    setSelectedEcoId(eco.ecosystem_id === selectedEcoId ? '' : eco.ecosystem_id);
                                    setActionErr('');
                                    setSuspendReason('');
                                    setSuspendCase('');
                                  }}
                                >
                                  <td>
                                    <div className="adm-mono">{eco.ecosystem_id}</div>
                                    <div className="text-tertiary">{eco.display_name || '—'}</div>
                                  </td>
                                  <td>
                                    <span className={`pill ${ecoKindTone(eco.kind)}`}>{ecoKindLabel(eco.kind)}</span>
                                  </td>
                                  <td>
                                    <span className={`pill ${ecoStatusTone(eco.status)}`}>{ecoStatusLabel(eco.status)}</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="table-wrap">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>生態系</th>
                          <th>類型</th>
                          <th>擁有組織</th>
                          <th>狀態</th>
                          <th>建立時間</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ecosystems.map((eco) => (
                          <tr
                            key={eco.ecosystem_id}
                            className={`clickable ${selectedEcoId === eco.ecosystem_id ? 'is-selected' : ''}`}
                            onClick={() => {
                              setSelectedEcoId(eco.ecosystem_id === selectedEcoId ? '' : eco.ecosystem_id);
                              setActionErr('');
                              setSuspendReason('');
                              setSuspendCase('');
                            }}
                          >
                            <td>
                              <div className="adm-mono">{eco.ecosystem_id}</div>
                              <div className="text-tertiary">{eco.display_name || '—'}</div>
                            </td>
                            <td>
                              <span className={`pill ${ecoKindTone(eco.kind)}`}>{ecoKindLabel(eco.kind)}</span>
                            </td>
                            <td className="adm-mono">{eco.owner_org_id || '(平台)'}</td>
                            <td>
                              <span className={`pill ${ecoStatusTone(eco.status)}`}>{ecoStatusLabel(eco.status)}</span>
                            </td>
                            <td>{formatTimestampUs(eco.created_at, { second: undefined })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              ) : (
                <div className="empty-state">
                  <div className="empty-state-icon">🌐</div>
                  <div className="empty-state-title">沒有符合篩選的生態系</div>
                  <div className="empty-state-detail">調整類型/狀態/owner org 篩選,或清空後重新套用。kinio.ai 平台生態系恆會列出。</div>
                </div>
              )}
            </section>

            <aside className="eco-side">
              <section className="card card--primary">
                <div className="section-head">
                  <h2 className="section-title">{selectedEco ? '生態系詳情' : '選取一個生態系'}</h2>
                </div>
                {!selectedEco ? (
                  <p className="eco-hint text-tertiary">在左側點選一個生態系以檢視來源鏈與治理動作。</p>
                ) : (
                  <div className="eco-form">
                    <div className="eco-kv-grid">
                      <div className="eco-kv">
                        <span className="eco-kv-label">生態系 ID</span>
                        <span className="eco-kv-value adm-mono">{selectedEco.ecosystem_id}</span>
                      </div>
                      <div className="eco-kv">
                        <span className="eco-kv-label">顯示名</span>
                        <span className="eco-kv-value">{selectedEco.display_name || '—'}</span>
                      </div>
                      <div className="eco-kv">
                        <span className="eco-kv-label">類型</span>
                        <span className="eco-kv-value">
                          <span className={`pill ${ecoKindTone(selectedEco.kind)}`}>{ecoKindLabel(selectedEco.kind)}</span>
                        </span>
                      </div>
                      <div className="eco-kv">
                        <span className="eco-kv-label">狀態</span>
                        <span className="eco-kv-value">
                          <span className={`pill ${ecoStatusTone(selectedEco.status)}`}>{ecoStatusLabel(selectedEco.status)}</span>
                        </span>
                      </div>
                      <div className="eco-kv">
                        <span className="eco-kv-label">擁有組織</span>
                        <span className="eco-kv-value adm-mono">{selectedEco.owner_org_id || '(平台)'}</span>
                      </div>
                      <div className="eco-kv">
                        <span className="eco-kv-label">建立時間</span>
                        <span className="eco-kv-value">{formatTimestampUs(selectedEco.created_at)}</span>
                      </div>
                      {selectedEco.reviewed_by && (
                        <div className="eco-kv">
                          <span className="eco-kv-label">審核人</span>
                          <span className="eco-kv-value adm-mono">{selectedEco.reviewed_by}</span>
                        </div>
                      )}
                      {selectedEco.reason && (
                        <div className="eco-kv">
                          <span className="eco-kv-label">審核/停權理由</span>
                          <span className="eco-kv-value">{selectedEco.reason}</span>
                        </div>
                      )}
                    </div>

                    {/* lineage:交叉比對創建審核紀錄 */}
                    <div className="section-head" style={{ marginTop: 'var(--spacing-4)' }}>
                      <h3 className="section-title">來源鏈</h3>
                    </div>
                    {selectedEcoLineage ? (
                      <div className="eco-kv-grid">
                        <div className="eco-kv">
                          <span className="eco-kv-label">創建申請</span>
                          <span className="eco-kv-value adm-mono">{selectedEcoLineage.request_id}</span>
                        </div>
                        <div className="eco-kv">
                          <span className="eco-kv-label">申請組織</span>
                          <span className="eco-kv-value adm-mono">{selectedEcoLineage.applicant_org_id}</span>
                        </div>
                        <div className="eco-kv">
                          <span className="eco-kv-label">審核結果</span>
                          <span className="eco-kv-value">
                            <span className={`pill ${requestStatusTone(selectedEcoLineage.status)}`}>{requestStatusLabel(selectedEcoLineage.status)}</span>
                          </span>
                        </div>
                        <div className="eco-kv">
                          <span className="eco-kv-label">審核人 / 案件</span>
                          <span className="eco-kv-value adm-mono">
                            {selectedEcoLineage.decided_by || '—'}
                            {selectedEcoLineage.case_id ? ` / ${selectedEcoLineage.case_id}` : ''}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <p className="eco-hint text-tertiary">
                        {isPlatformEcosystem(selectedEco)
                          ? '平台生態系(kinio.ai)為系統種子,無創建審核申請。'
                          : selectedEco.kind === 'org_default'
                            ? '組織預設生態系(第 1 個)免審直接生效,無創建審核申請。'
                            : '找不到對應創建申請(可能建立於審核紀錄之外,或已超出查詢範圍)。'}
                      </p>
                    )}

                    {/* admin#6:直接成員清單 */}
                    <div className="section-head" style={{ marginTop: 'var(--spacing-4)' }}>
                      <h3 className="section-title">直接成員</h3>
                      {memberLoading && <span className="pill pill-neutral">查詢中</span>}
                    </div>
                    {memberErr ? (
                      <ErrorState error={memberErr} onRetry={() => void loadMembers(selectedEco.ecosystem_id)} />
                    ) : memberView ? (
                      <div className="eco-form">
                        <div className="eco-kv">
                          <span className="eco-kv-label">直接成員</span>
                          <span className="eco-kv-value">
                            成員 org {memberView.member_orgs.length} 個・巢狀子生態系 {memberView.sub_ecosystems.length} 個
                          </span>
                        </div>
                        {memberView.member_orgs.length > 0 && (
                          <div className="table-wrap">
                            <table className="data-table">
                              <thead>
                                <tr>
                                  <th>成員組織</th>
                                  <th>狀態</th>
                                  <th>加入</th>
                                </tr>
                              </thead>
                              <tbody>
                                {memberView.member_orgs.map((m) => (
                                  <tr key={m.org_id}>
                                    <td>
                                      <div className="adm-mono">{m.org_id}</div>
                                      <div className="text-tertiary">{m.org_name || '—'}</div>
                                    </td>
                                    <td>
                                      <span className={`pill ${memberStatusTone(m.status)}`}>{memberStatusLabel(m.status)}</span>
                                    </td>
                                    <td className="text-tertiary">
                                      {m.added_via || '—'}
                                      {m.added_at ? `・${formatTimestampUs(m.added_at, { second: undefined })}` : ''}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        {memberView.sub_ecosystems.length > 0 && (
                          <div className="table-wrap">
                            <table className="data-table">
                              <thead>
                                <tr>
                                  <th>巢狀子生態系</th>
                                  <th>類型</th>
                                  <th>成員狀態</th>
                                </tr>
                              </thead>
                              <tbody>
                                {memberView.sub_ecosystems.map((s) => (
                                  <tr key={s.ecosystem_id}>
                                    <td>
                                      <div className="adm-mono">{s.ecosystem_id}</div>
                                      <div className="text-tertiary">{s.display_name || '—'}</div>
                                    </td>
                                    <td>
                                      <span className={`pill ${ecoKindTone(s.kind)}`}>{ecoKindLabel(s.kind)}</span>
                                    </td>
                                    <td>
                                      <span className={`pill ${memberStatusTone(s.member_status)}`}>{memberStatusLabel(s.member_status)}</span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        {memberView.member_orgs.length === 0 && memberView.sub_ecosystems.length === 0 && (
                          <p className="eco-hint text-tertiary">此生態系目前沒有直接成員(無成員 org、無巢狀子生態系)。</p>
                        )}
                      </div>
                    ) : (
                      !memberLoading && <p className="eco-hint text-tertiary">選取後載入直接成員…</p>
                    )}

                    {/* admin#6:effective 遞移展開 */}
                    <div className="section-head" style={{ marginTop: 'var(--spacing-4)' }}>
                      <h3 className="section-title">有效範圍展開</h3>
                      {effLoading && <span className="pill pill-neutral">展開中</span>}
                    </div>
                    {!showEffective ? (
                      <>
                        <button className="btn-ghost btn-sm" disabled={effLoading} onClick={() => void loadEffective(selectedEco.ecosystem_id)}>
                          展開 effective 閉包
                        </button>
                        <span className="eco-hint text-tertiary">
                          從此生態系出發的遞移閉包(both member axes、深度上限 3),與 enforcement resolver 同一套 BFS。
                          非 active 生態系(suspended/pending)展開為空。
                        </span>
                      </>
                    ) : effErr ? (
                      <ErrorState error={effErr} onRetry={() => void loadEffective(selectedEco.ecosystem_id)} />
                    ) : effective ? (
                      <div className="eco-form">
                        {effective.truncated && (
                          <p className="eco-protected-note">
                            <span aria-hidden="true">⚠️</span>
                            有成員鏈觸及深度上限(3)未再展開(truncated);更深的巢狀不列入此視角。
                          </p>
                        )}
                        <div className="eco-kv">
                          <span className="eco-kv-label">遞移閉包</span>
                          <span className="eco-kv-value">
                            可達生態系 {effective.ecosystems.length} 個・可達成員 org {effective.orgs.length} 個
                          </span>
                        </div>
                        {effective.ecosystems.length > 0 && (
                          <div className="table-wrap">
                            <table className="data-table">
                              <thead>
                                <tr>
                                  <th>可達生態系</th>
                                  <th>類型</th>
                                  <th>深度</th>
                                  <th>授權路徑</th>
                                </tr>
                              </thead>
                              <tbody>
                                {[...effective.ecosystems]
                                  .sort((a, b) => a.depth - b.depth)
                                  .map((e) => (
                                    <tr key={e.ecosystem_id}>
                                      <td className="adm-mono">{e.ecosystem_id}</td>
                                      <td>
                                        <span className={`pill ${ecoKindTone(e.kind)}`}>{ecoKindLabel(e.kind)}</span>
                                      </td>
                                      <td>{e.depth}</td>
                                      <td className="adm-mono text-tertiary">{formatViaChain(e.via_chain)}</td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        {effective.orgs.length > 0 ? (
                          <div className="table-wrap">
                            <table className="data-table">
                              <thead>
                                <tr>
                                  <th>可達成員組織</th>
                                  <th>經由生態系</th>
                                  <th>授權路徑</th>
                                </tr>
                              </thead>
                              <tbody>
                                {effective.orgs.map((o) => (
                                  <tr key={`${o.org_id}:${o.via_ecosystem}`}>
                                    <td className="adm-mono">{o.org_id}</td>
                                    <td className="adm-mono">{o.via_ecosystem}</td>
                                    <td className="adm-mono text-tertiary">{formatViaChain(o.via_chain)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="eco-hint text-tertiary">
                            {isPlatformEcosystem(selectedEco)
                              ? 'kinio.ai 平台生態系不列舉 union 成員 org 集(平台級互通以 platform_union 旗標表示,非逐一列舉)。'
                              : effective.ecosystems.length === 0
                                ? '此生態系目前非 active(或無互通關係),effective 展開為空。'
                                : '無可達的其他成員 org。'}
                          </p>
                        )}
                      </div>
                    ) : null}

                    {/* 治理動作:suspend */}
                    <div className="section-head" style={{ marginTop: 'var(--spacing-4)' }}>
                      <h3 className="section-title">平台治理</h3>
                    </div>
                    {isPlatformEcosystem(selectedEco) ? (
                      <p className="eco-protected-note">
                        <span aria-hidden="true">🔒</span>
                        kinio.ai 為平台生態系(kind=platform):依業主定案不可退出 / 刪除 / 停權,本頁不提供任何破壞性操作。
                      </p>
                    ) : !canSuspend(selectedEco) ? (
                      <p className="eco-hint text-tertiary">
                        目前狀態為「{ecoStatusLabel(selectedEco.status)}」,無可用的停權動作
                        {selectedEco.status === 'suspended' ? '(已停權)' : ''}。
                      </p>
                    ) : writable ? (
                      <>
                        <label className="adm-field">
                          <span className="adm-field-label text-secondary">停權理由(必填)</span>
                          <input value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)} disabled={busy} placeholder="停權此生態系的原因" />
                        </label>
                        <label className="adm-field">
                          <span className="adm-field-label text-secondary">案件編號(必填)</span>
                          <input value={suspendCase} onChange={(e) => setSuspendCase(e.target.value)} disabled={busy} placeholder="CASE-…" />
                        </label>
                        <button
                          className="btn-danger"
                          disabled={busy || !suspendReady}
                          onClick={() => {
                            setActionErr('');
                            setConfirmSuspend(true);
                          }}
                        >
                          停權生態系
                        </button>
                        <span className="eco-hint text-tertiary">
                          停權為可逆治理動作(採二段確認 + 全鏈稽核 admin.ecosystem.suspend);retire 尚無 admin 端點。
                        </span>
                      </>
                    ) : (
                      <p className="eco-hint text-tertiary">停權由 admin / platform-owner 執行(目前角色唯讀)。</p>
                    )}
                  </div>
                )}
              </section>
            </aside>
          </div>
        </>
      )}

      {confirmDecision && selectedReq && (
        <ConfirmDialog
          title={confirmDecision === 'approve' ? '核准創建申請' : '駁回創建申請'}
          tone={confirmDecision === 'approve' ? 'primary' : 'danger'}
          confirmLabel={confirmDecision === 'approve' ? '確認核准' : '確認駁回'}
          busy={busy}
          error={actionErr || undefined}
          body={
            <>
              <p>
                將{confirmDecision === 'approve' ? '核准' : '駁回'} org <span className="adm-mono">{selectedReq.applicant_org_id}</span> 的創建申請
                {selectedReq.ecosystem_id ? (
                  <>
                    ,生態系 <span className="adm-mono">{selectedReq.ecosystem_id}</span> 將
                    {confirmDecision === 'approve' ? ' 由待審核轉為生效中' : ' 標記為已駁回'}。
                  </>
                ) : (
                  '。'
                )}
              </p>
              <p className="text-tertiary">
                處置理由:{decideReason.trim()}・案件:{decideCase.trim()}(將寫入稽核,含 actor)
              </p>
            </>
          }
          onConfirm={() => void submitDecision()}
          onCancel={() => setConfirmDecision(null)}
        />
      )}

      {confirmSuspend && selectedEco && (
        <ConfirmDialog
          title="停權生態系"
          tone="danger"
          confirmLabel="確認停權"
          busy={busy}
          error={actionErr || undefined}
          warning="停權後該生態系的互通關係將被平台凍結。此為可逆治理動作(可另循流程恢復),但仍會即時影響成員 org。"
          body={
            <>
              <p>
                將停權生態系 <span className="adm-mono">{selectedEco.ecosystem_id}</span>
                {selectedEco.display_name ? `(${selectedEco.display_name})` : ''}(owner org {selectedEco.owner_org_id || '(平台)'})。
              </p>
              <p className="text-tertiary">
                停權理由:{suspendReason.trim()}・案件:{suspendCase.trim()}(將寫入稽核 admin.ecosystem.suspend,含 actor)
              </p>
            </>
          }
          onConfirm={() => void submitSuspend()}
          onCancel={() => setConfirmSuspend(false)}
        />
      )}
    </div>
  );
}
